import { useMemo, useState } from "react";
import { Check, Flag } from "lucide-react";

import { DataTable, type DataTableColumn } from "@/components/cinch/DataTable/DataTable";
import {
  DateRangeChip,
  FilterBar,
  FilterClearAll,
  MultiSelectChip,
} from "@/components/cinch/FilterBar";
import type { DateRange } from "@/components/cinch/DatePicker";
import { EntityName } from "@/components/cinch/EntityName";
import { StatusBadge } from "@/components/cinch/StatusBadge";
import { useCan } from "@/lib/auth";

import {
  severityBadgeValue,
  useIncidents,
  useOverview,
  useReviewIncident,
  type IncidentOut,
  type SignalOut,
} from "../api";
import { entityContext, entitySubject } from "../display";
import { SignalStrip } from "./SignalStrip";

const STATE_OPTIONS = [
  { value: "open", label: "Open" },
  { value: "resolved", label: "Resolved" },
  { value: "dormant", label: "Dormant" },
];
const SEVERITY_OPTIONS = [
  { value: "critical", label: "Critical" },
  { value: "alert", label: "Alert" },
];
const LEVEL_OPTIONS = [
  { value: "L1", label: "L1 — integration" },
  { value: "L2", label: "L2 — company" },
  { value: "L3", label: "L3 — location" },
];

/** The daily working view. Columns exactly per decision #7: Severity · Level
 *  · Source/Company/Location · Days · Date — everything else is in the
 *  drawer. Row quick-✓ approves in one click; the flag opens the drawer with
 *  the notes box focused (reconciliation's interaction, verbatim). */
export function AlertsTab({
  onOpenIncident,
  onOpenSignal,
}: {
  onOpenIncident: (id: string, presetFlag?: boolean) => void;
  onOpenSignal: (s: SignalOut) => void;
}) {
  const canWrite = useCan("integrations.write");
  const [states, setStates] = useState<string[]>(["open"]);
  const [severities, setSeverities] = useState<string[]>([]);
  const [levels, setLevels] = useState<string[]>([]);
  const [range, setRange] = useState<DateRange>({ start: null, end: null });
  const [offset, setOffset] = useState(0);
  const limit = 50;

  const filters = useMemo(
    () => ({
      state: (states.length === 1 ? states[0] : undefined) ?? "all",
      severity: severities.length === 1 ? severities[0] : undefined,
      level: levels.length === 1 ? levels[0] : undefined,
      opened_after: range.start ? toIsoDay(range.start) : undefined,
      opened_before: range.end ? toIsoDay(range.end) : undefined,
      limit,
      offset,
    }),
    [states, severities, levels, range, offset],
  );
  const incidents = useIncidents(filters);
  const overview = useOverview();
  const review = useReviewIncident();

  const anyFilter =
    states.length !== 1 || states[0] !== "open" || severities.length > 0 ||
    levels.length > 0 || range.start != null || range.end != null;

  const columns: DataTableColumn<IncidentOut>[] = [
    {
      key: "severity",
      header: "Severity",
      render: (r) => <StatusBadge value={severityBadgeValue(r)} />,
    },
    {
      key: "key_path",
      header: "What broke — and where",
      // The SUBJECT leads, its ancestry sits underneath. Leading with the
      // source made an L2/L3 break read as "the whole integration is down":
      // an alert on one Jiffy Lube company rendered as "meraki_grow" in bold
      // with the company demoted to grey. The row is about the company.
      render: (r) => {
        const subject = entitySubject(r);
        return (
          <EntityName
            name={subject.name}
            fallback={subject.id || r.key_path}
            context={entityContext(r)}
            badge={<StatusBadge value={r.level} label={subject.levelLabel} />}
          />
        );
      },
    },
    {
      key: "streak_days",
      header: "Days",
      numeric: true,
      render: (r) =>
        r.state === "dormant" ? (
          <span className="text-xs text-muted-foreground">
            dormant since {r.last_bad_day ?? r.opened_on}
          </span>
        ) : (
          r.streak_days
        ),
    },
    { key: "opened_on", header: "Date", sortable: false },
    {
      key: "_review",
      header: "Review",
      render: (r) => (
        <div className="flex items-center gap-1.5">
          <StatusBadge value={r.review_status} />
          {r.review_superseded && <StatusBadge value="flagged" label="Superseded" />}
          {canWrite && r.review_status === "pending" && (
            <>
              <button
                type="button"
                title="Expected — we understand this (one click)"
                className="rounded p-1 text-success hover:bg-success-light"
                onClick={(e) => {
                  e.stopPropagation();
                  review.mutate({ id: r.id, status: "approved" });
                }}
              >
                <Check className="h-4 w-4" />
              </button>
              <button
                type="button"
                title="Real issue — opens notes"
                className="rounded p-1 text-warning hover:bg-warning-light"
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenIncident(r.id, true);
                }}
              >
                <Flag className="h-4 w-4" />
              </button>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      {overview.data && (
        <SignalStrip signals={overview.data.signals} onOpen={onOpenSignal} />
      )}
      <FilterBar>
        <MultiSelectChip label="State" options={STATE_OPTIONS} value={states} onChange={(v) => { setStates(v); setOffset(0); }} />
        <MultiSelectChip label="Severity" options={SEVERITY_OPTIONS} value={severities} onChange={(v) => { setSeverities(v); setOffset(0); }} />
        <MultiSelectChip label="Level" options={LEVEL_OPTIONS} value={levels} onChange={(v) => { setLevels(v); setOffset(0); }} />
        <DateRangeChip label="Opened" value={range} onChange={(r) => { setRange(r); setOffset(0); }} />
        <div className="flex-1" />
        {anyFilter && (
          <FilterClearAll
            onClick={() => {
              setStates(["open"]);
              setSeverities([]);
              setLevels([]);
              setRange({ start: null, end: null });
              setOffset(0);
            }}
          />
        )}
      </FilterBar>
      <DataTable
        columns={columns}
        rows={incidents.data?.items ?? []}
        rowKey={(r) => r.id}
        loading={incidents.isLoading}
        error={incidents.error ? String(incidents.error) : null}
        pagination={{
          limit,
          offset,
          count: incidents.data?.count ?? 0,
        }}
        onPageChange={setOffset}
        onRowClick={(r) => onOpenIncident(r.id)}
        emptyMessage="No incidents — which is the goal. Loud things here are rare and real."
      />
    </div>
  );
}

function toIsoDay(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
